// One product, everything ops can do with it: where it stands in Autotask and
// what to do about it, its fields (saved to Autotask in the same request), Omni's
// own pricing, its OneBill product, and whether dealers see it. Its product code is
// read-only (#2057): Omni reads it from Autotask, then KQM, and never writes it.
import React, { useState } from "react";
import { codeSourceOf, formatCents, type CatalogPatchV2 } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { centsToDollars, parseDollars, whenIn } from "../../format.js";
import { Callout, Chip, Sheet } from "../../ui.js";
import { useCatalog } from "./context.js";
import { FIELD_LABEL, atRef, billedText, blockers, categoriesOf, clock, copyNote, createDiff, fieldValue, kindWord, perText, type Product } from "./model.js";
import { AtChip, CreateDiffTable, LinkChip, OneBillPicker, PriceBox } from "./parts.js";

/** The product code, where Omni read it, and where to change it. Never an input. */
function CodeRow({ p }: { p: Product }): React.ReactElement {
  const { checked } = useCatalog();
  const code = p.code.trim();
  const source = codeSourceOf(p);
  const conflict = checked?.codes?.conflicts.find((c) => c.product.id === p.id) ?? null;
  const at = checked?.lastOkAt ? ` at the ${clock(checked.lastOkAt)} check` : "";
  let hint: string;
  if (!code) {
    hint = p.autotask
      ? `Neither Autotask nor KQM has a code for this product. Add one to ${atRef(p)} in Autotask and the next check reads it.`
      : "Neither Autotask nor KQM has a code for this product. Once it is in Autotask, add one there and the next check reads it.";
  } else if (source === "kqm") {
    hint = `Read from KQM: the part number of the KQM product titled \u201c${p.name}\u201d. Autotask has no code for ${atRef(p)}. To change it, change it in KQM, or add one in Autotask, which wins.`;
  } else if (p.autotask) {
    hint = `Read from ${atRef(p)}\u2019s manufacturer field, else its SKU${at}. To change it, change it in Autotask.`;
  } else {
    hint = `Omni writes it to Autotask when it creates ${atRef(p)}, then reads it from Autotask. To change it after that, change it in Autotask.`;
  }
  return (
    <div className="field" data-testid="sheet-code" data-source={code ? (source ?? "") : "none"}>
      <span>Product code</span>
      <div className="ro" data-testid="code-ro">
        {code ? (
          <>
            <b className="pc-codeval">{code}</b>
            <Chip kind="quiet" icon="lock" testId="code-source">
              {source === "kqm" ? "From KQM" : "From Autotask"}
            </Chip>
          </>
        ) : (
          <span className="muted">None</span>
        )}
      </div>
      <span className="hint" data-testid="code-hint">
        {hint}
      </span>
      {conflict && conflict.autotask ? (
        <span className="hint pc-warn" data-testid="code-conflict">
          <Icon name="arrow-right-left" size={12} /> KQM says {conflict.kqm.join(" or ")}. Omni uses Autotask{"\u2019"}s.
        </span>
      ) : conflict ? (
        <span className="hint pc-warn" data-testid="code-conflict">
          <Icon name="arrow-right-left" size={12} /> KQM has more than one part number for this title ({conflict.kqm.join(", ")}), so Omni uses none. Settle it in KQM.
        </span>
      ) : null}
    </div>
  );
}

function AutotaskBlock({ p }: { p: Product }): React.ReactElement {
  const { autotask, diffs, busy, links } = useCatalog();
  const sync = p.autotaskSync;
  if (sync.state === "in_sync") {
    return (
      <div className="state-line">
        <Icon name="circle-check" size={15} />
        <span>
          Matches {atRef(p)}
          {sync.checkedAt ? ` as of the check ${whenIn(sync.checkedAt)}` : ""}.
        </span>
      </div>
    );
  }
  if (sync.state === "creating") {
    return (
      <div className="callout" role="status">
        <Icon name="refresh-cw" size={15} className="spin-ic" />
        <span>
          <b>Creating in Autotask.</b> Omni sent it{sync.pushedAt ? ` ${whenIn(sync.pushedAt)}` : ""} as a {kindWord(p)} and is reading it back. Dealers can{"\u2019"}t quote it until
          it{"\u2019"}s there.
        </span>
      </div>
    );
  }
  if (sync.state === "push_failed") {
    return (
      <>
        <div className="callout stop" role="alert">
          <Icon name="triangle-alert" size={15} />
          <span>
            <b>Push failed{sync.pushedAt ? ` ${whenIn(sync.pushedAt)}` : ""}.</b> {sync.error ?? ""} Omni kept the product, and dealers can{"\u2019"}t quote it until it{"\u2019"}s in Autotask.
            <span className="block">Retry looks for it in Autotask first, so it can{"\u2019"}t create a second one.</span>
          </span>
        </div>
        {links ? (
          <div className="btnrow">
            <button className="btn primary" type="button" disabled={busy} data-testid="retry" onClick={() => void autotask(p, "retry")}>
              <Icon name="refresh-cw" size={15} />
              Retry
            </button>
          </div>
        ) : null}
      </>
    );
  }
  if (sync.state === "changed_in_autotask") {
    const active = sync.differences.some((d) => d.field === "active");
    return (
      <>
        <div className="callout warn" role="status">
          <Icon name="arrow-right-left" size={15} />
          <span>
            <b>Changed in Autotask.</b> The check{sync.checkedAt ? ` ${whenIn(sync.checkedAt)}` : ""} found {sync.differences.length === 1 ? "1 difference" : `${sync.differences.length} differences`} from what Omni
            wrote. Omni doesn{"\u2019"}t apply or undo it on its own.{" "}
            {active ? "Dealers can\u2019t quote it until you settle it, because Active differs." : "Dealers keep quoting Omni\u2019s values until you settle it."}
          </span>
        </div>
        <DiffTable p={p} />
        {links ? (
          <div className="btnrow">
            <button className="btn primary" type="button" data-testid="review-diffs" onClick={() => diffs(p.id)}>
              Review differences
            </button>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <>
      <div className="callout stop" role="alert">
        <Icon name="ban" size={15} />
        <span>
          <b>Missing in Autotask.</b> The check{sync.checkedAt ? ` ${whenIn(sync.checkedAt)}` : ""} found no {kindWord(p)} {p.autotask?.id ?? ""} in Autotask. Someone deleted it there. Dealers can{"\u2019"}t
          quote it.
          <span className="block">Create again makes a new {kindWord(p)} from Omni{"\u2019"}s values and links it.</span>
        </span>
      </div>
      {links ? (
        <div className="btnrow">
          <button className="btn primary" type="button" disabled={busy} data-testid="create-again" onClick={() => void autotask(p, "create_again")}>
            <Icon name="plus" size={15} />
            Create again
          </button>
        </div>
      ) : null}
    </>
  );
}

export function DiffTable({ p, testId }: { p: Product; testId?: string }): React.ReactElement {
  return (
    <table className="diff" data-testid={testId}>
      <thead>
        <tr>
          <th>Field</th>
          <th>In Omni</th>
          <th>In Autotask</th>
        </tr>
      </thead>
      <tbody>
        {p.autotaskSync.differences.map((d) => (
          <tr key={d.field}>
            <td>{FIELD_LABEL[d.field]}</td>
            <td>{fieldValue(d, d.omni)}</td>
            <td>{fieldValue(d, d.autotask)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Why a check linked this product on its own: by its code, by name for a product without one, or by name before Omni read codes. */
function autoWhy(p: Product): string {
  const code = p.code.trim();
  if (p.billingLink.tier === "code" && code) return `Linked automatically: OneBill has one product with code ${code}, and one plan that bills the same way.`;
  if (p.billingLink.tier === "exact_name" || p.billingLink.tier === "product_key") return "Linked automatically by name: this product has no code, and OneBill has one product with its name and one plan that bills the same way.";
  return "Linked automatically by a check, by name, before Omni read codes.";
}

/** The last Create in OneBill that failed (#2156): when, what OneBill said, and each field when its product differs from the one it copied. */
function CreateFailed({ error }: { error: { at: string; message: string } }): React.ReactElement {
  const diff = createDiff(error.message);
  return (
    <>
      <div className="callout stop" role="alert" data-testid="create-error">
        <Icon name="triangle-alert" size={15} />
        <span>
          <b>Creating it in OneBill failed {whenIn(error.at)}.</b> {diff ? `${diff.head}. ${diff.tail}` : error.message}
          {!diff ? (
            <span className="block">Omni linked nothing. Creating it again looks for it in OneBill first, so it can{"\u2019"}t make a second one.</span>
          ) : diff.created ? (
            <span className="block">
              Fix or delete it in OneBill, then apply again. Omni checks it against {diff.template} first and links it only if it matches.
            </span>
          ) : null}
        </span>
      </div>
      {diff ? <CreateDiffTable diff={diff} testId="create-error-diff" /> : null}
    </>
  );
}

function OneBillBlock({ p }: { p: Product }): React.ReactElement {
  const { link, decide, busy, links } = useCatalog();
  const [sameCode, setSameCode] = useState<boolean | null>(null);
  // Create in OneBill asks which OneBill plan's setup to copy before anything is saved (#2156).
  const [copying, setCopying] = useState(false);
  const l = p.billingLink;
  const code = p.code.trim();
  const who = l.via === "auto" ? (
    <p className="hint first" data-testid="link-why">
      {autoWhy(p)}
    </p>
  ) : l.reviewedBy && l.state !== "unreviewed" ? (
    <p className="hint first" data-testid="onebill-who">
      {l.state === "linked" ? "Confirmed" : "Chosen"} by {l.reviewedBy}
      {l.reviewedAt ? ` ${whenIn(l.reviewedAt)}` : ""}.
    </p>
  ) : null;
  const againButton = (
    <button className="btn" type="button" disabled={busy} data-testid="onebill-again" onClick={() => void decide(p, "unreviewed")}>
      <Icon name="undo-2" size={15} />
      {l.state === "linked" ? (l.via === "auto" ? "Undo automatic link" : "Change link") : "Review again"}
    </button>
  );
  const again = !links ? null : <div className="btnrow">{againButton}</div>;
  const viewOnly = (
    <p className="card-p ro" data-testid="sheet-link-view-only">
      <Icon name="lock" size={14} />
      <span>Billing ops or a Super admin links it to OneBill.</span>
    </p>
  );
  // OneBill refuses a product without a description, so say so before anyone applies.
  const noDescription = p.description.trim() ? null : (
    <div className="callout warn" data-testid="no-description">
      <Icon name="triangle-alert" size={15} />
      <span>
        <b>OneBill requires a description, and this product has none.</b> Add one under Product above, then save.
      </span>
    </div>
  );
  const copyPicker = (
    <OneBillPicker
      p={p}
      where="sheet"
      busy={busy}
      mode="copy"
      onCopy={(productCode, pricePlanId) => {
        void link(p, { state: "create", copyFrom: { productCode, pricePlanId } }, copyNote(p.name, { productCode, pricePlanId })).then((saved) => {
          if (saved) setCopying(false);
        });
      }}
    />
  );
  if (l.state === "linked" && p.onebill) {
    return (
      <>
        {who}
        <dl className="facts">
          <div>
            <dt>OneBill product</dt>
            <dd data-testid="onebill-product">{p.onebill.productCode}</dd>
          </div>
          <div>
            <dt>Price plan</dt>
            <dd data-testid="onebill-plan">{p.onebill.pricePlanId}</dd>
          </div>
        </dl>
        <p className="hint">Omni orders bill on this plan, each at the price on its quote.</p>
        {again}
      </>
    );
  }
  if (l.state === "create") {
    const failed = l.createError ? <CreateFailed error={l.createError} /> : null;
    if (!l.copyFrom || copying) {
      return (
        <>
          {who}
          {failed}
          {l.copyFrom ? null : (
            <div className="callout warn" data-testid="no-copy-source">
              <Icon name="triangle-alert" size={15} />
              <span>Marked for creation, but no OneBill product chosen to copy setup from.</span>
            </div>
          )}
          {links ? (
            <>
              {noDescription}
              {copyPicker}
              <div className="btnrow">
                {l.copyFrom ? (
                  <button className="btn" type="button" data-testid="copy-cancel" onClick={() => setCopying(false)}>
                    <Icon name="arrow-left" size={15} />
                    Cancel
                  </button>
                ) : null}
                {againButton}
              </div>
            </>
          ) : (
            viewOnly
          )}
        </>
      );
    }
    return (
      <>
        {who}
        {failed}
        {noDescription}
        <dl className="facts">
          <div>
            <dt>Copy setup from</dt>
            <dd data-testid="copy-from-product">{l.copyFrom.productCode}</dd>
          </div>
          <div>
            <dt>Price plan</dt>
            <dd data-testid="copy-from-plan">{l.copyFrom.pricePlanId}</dd>
          </div>
        </dl>
        <p className="hint" data-testid="copy-rule">
          The next sync creates it in OneBill with this plan{"\u2019"}s billing, tax, GL and feature setup, at the list price, {formatCents(p.listPrice)}
          {perText(p.billing)}. Omni reads it back and links it only if it matches {l.copyFrom.productCode}.
        </p>
        {links ? (
          <div className="btnrow">
            <button className="btn" type="button" disabled={busy} data-testid="copy-change" onClick={() => setCopying(true)}>
              <Icon name="copy" size={15} />
              Change setup
            </button>
            {againButton}
          </div>
        ) : null}
      </>
    );
  }
  if (l.state === "not_billed") {
    return (
      <>
        {who}
        <p className="hint">OneBill doesn{"\u2019"}t bill this, so Omni never quotes it and dealers don{"\u2019"}t see it.</p>
        {again}
      </>
    );
  }
  if (links && copying) {
    return (
      <>
        {noDescription}
        {copyPicker}
        <div className="btnrow">
          <button className="btn" type="button" data-testid="sheet-copy-back" onClick={() => setCopying(false)}>
            <Icon name="arrow-left" size={15} />
            Back to linking
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <p className="hint first" data-testid="ob-hint">
        {sameCode && code
          ? `OneBill has a product with code ${code}. It\u2019s first below: confirm the plan it bills on.`
          : sameCode === false && code
            ? `OneBill has no product with code ${code}, so a person confirms which OneBill product and plan this bills on. Until then dealers can\u2019t quote it.`
            : !code
              ? "This product has no code, so a person confirms which OneBill product and plan this bills on. Until then dealers can\u2019t quote it."
              : "A person confirms which OneBill product and plan this bills on. Until then dealers can\u2019t quote it."}
      </p>
      {links ? null : viewOnly}
      {links ? (
        <>
          <OneBillPicker
            p={p}
            where="sheet"
            busy={busy}
            onSameCode={setSameCode}
            onLink={(productCode, pricePlanId) => void link(p, { state: "linked", onebill: { productCode, pricePlanId } }, `Linked ${p.name} to OneBill ${productCode}, plan ${pricePlanId}.`)}
          />
          <div className="btnrow">
            <button className="btn" type="button" disabled={busy} data-testid="sheet-create" onClick={() => setCopying(true)}>
              <Icon name="plus" size={15} />
              Create in OneBill
            </button>
            <button className="btn" type="button" disabled={busy} data-testid="sheet-notq" onClick={() => void decide(p, "not_billed")}>
              <Icon name="ban" size={15} />
              Not quoted in Omni
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

const dollars = (c: number | null) => (c === null ? "" : centsToDollars(c));

export function ProductSheet({ p }: { p: Product }): React.ReactElement {
  const { list, enable, save, close, refused, problem, busy, checked, manages } = useCatalog();
  const [name, setName] = useState(p.name);
  const [category, setCategory] = useState(p.category);
  const [description, setDescription] = useState(p.description);
  const [unit, setUnit] = useState(p.unit);
  const [listPrice, setListPrice] = useState(dollars(p.listPrice));
  const [unitCost, setUnitCost] = useState(dollars(p.unitCost));
  const [active, setActive] = useState(p.active);
  const [floor, setFloor] = useState(dollars(p.floorPrice));
  const [buy, setBuy] = useState(dollars(p.wholesaleCost));
  const [formError, setFormError] = useState<string | null>(null);
  const reasons = blockers(p);
  const isOneTime = p.billing === "one_time" || p.autotask?.kind === "product";
  const atCategories = checked?.categories && checked.categories.length > 0 ? checked.categories : [];
  const categories = isOneTime ? atCategories : categoriesOf([...list, { category }]);

  async function submit(): Promise<void> {
    const optional = (text: string) => (text.trim() === "" ? null : parseDollars(text));
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError("A product needs a name.");
      return;
    }
    const prices = { listPrice: parseDollars(listPrice), floorPrice: parseDollars(floor), unitCost: optional(unitCost), wholesaleCost: optional(buy) };
    if (prices.listPrice === null || prices.floorPrice === null || (unitCost.trim() !== "" && prices.unitCost === null) || (buy.trim() !== "" && prices.wholesaleCost === null)) {
      setFormError("Prices need to be amounts in dollars, like 19.00.");
      return;
    }
    setFormError(null);
    const next = {
      name: trimmed,
      category,
      description: description.trim(),
      unit: unit.trim(),
      listPrice: prices.listPrice,
      unitCost: prices.unitCost,
      active,
      floorPrice: prices.floorPrice,
      wholesaleCost: prices.wholesaleCost,
    };
    const patch: Omit<CatalogPatchV2, "expectedUpdatedAt"> = {};
    for (const key of Object.keys(next) as Array<keyof typeof next>) {
      if (next[key] !== p[key]) Object.assign(patch, { [key]: next[key] });
    }
    if (Object.keys(patch).length === 0) {
      close();
      return;
    }
    if (await save(p, patch)) close();
  }

  return (
    <Sheet
      title={p.name}
      wide
      onClose={close}
      foot={
        <>
          <button className="btn" type="button" onClick={close}>
            Close
          </button>
          {manages ? (
            <button className="btn primary" type="button" disabled={busy} data-testid="save" onClick={() => void submit()}>
              <Icon name="check" size={15} />
              Save
            </button>
          ) : null}
        </>
      }
    >
      <div className="omni-cat" data-testid="product-sheet" data-product={p.id}>
        {problem ? (
          <Callout kind="stop" icon="triangle-alert">
            {problem}
          </Callout>
        ) : null}
        <div className="sec">
          <div className="chiprow">
            <AtChip p={p} />
            <LinkChip p={p} />
            {p.enabled ? (
              <Chip kind="ok" icon="eye">
                Dealers see it
              </Chip>
            ) : (
              <Chip kind="quiet">Switched off</Chip>
            )}
            {p.active ? null : <Chip kind="quiet">Inactive</Chip>}
          </div>
          <dl className="facts">
            <div>
              <dt>In Autotask</dt>
              <dd>{p.autotask ? `${kindWord(p)} ${p.autotask.id}` : "Not created yet"}</dd>
            </div>
            <div>
              <dt>Billed</dt>
              <dd>{billedText(p)}</dd>
            </div>
            <div>
              <dt>OneBill</dt>
              <dd>{p.onebill ? `${p.onebill.productCode} \u00b7 plan ${p.onebill.pricePlanId}` : "Not linked"}</dd>
            </div>
          </dl>
        </div>

        <div className="sec" data-testid="sheet-autotask" data-state={p.autotaskSync.state}>
          <div className="sec-head">
            <h3>Autotask</h3>
            <AtChip p={p} />
          </div>
          <AutotaskBlock p={p} />
        </div>

        <div className="sec">
          <div className="sec-head">
            <h3>Product</h3>
            <span className="note">{manages ? "Saving writes these to Autotask, then reads it back" : "A Super admin changes these"}</span>
          </div>
          <fieldset className="form2 plain" disabled={!manages}>
            <label className="field full">
              <span>Name</span>
              <input className="input" data-testid="f-name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <CodeRow p={p} />
            <label className="field">
              <span>Category</span>
              <select className="input" data-testid="f-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {isOneTime && category && !categories.includes(category) ? (
                  <option value={category} disabled>
                    {category} (invalid)
                  </option>
                ) : null}
                {categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              {p.autotask?.kind === "service" ? <span className="hint">Autotask Services have no category, so this one lives in Omni only.</span> : null}
            </label>
            <label className="field full">
              <span>Description</span>
              <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="field">
              <span>List price</span>
              <PriceBox id="f-list" label="List price" value={listPrice} onChange={setListPrice} />
              <span className="hint">{p.billing === "monthly" ? "A month." : p.billing ? "Once." : `${billedText(p)}.`}</span>
            </label>
            <label className="field">
              <span>Unit cost</span>
              <PriceBox id="f-cost" label="Unit cost" value={unitCost} placeholder="Not set" onChange={setUnitCost} />
              <span className="hint">Phoneware{"\u2019"}s cost. Ops only.</span>
            </label>
            <label className="field">
              <span>Unit</span>
              <input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} />
            </label>
            <div className="field">
              <span>Billed</span>
              <div className="ro">
                {p.billing ? <span>{billedText(p)}</span> : <Chip kind="warn" icon="clock">{billedText(p)}</Chip>}
                <span className="muted small">an Autotask {kindWord(p)}</span>
              </div>
              <span className="hint">Set when a product is created. To bill it another way, add a new product.</span>
            </div>
            <div className="field">
              <span id="f-active-label">Active</span>
              <div className="ro">
                <label className="switch">
                  <input type="checkbox" aria-labelledby="f-active-label" data-testid="f-active" checked={active} onChange={(e) => setActive(e.target.checked)} />
                  <i />
                </label>
                <span className="small muted">Mirrored to Autotask</span>
              </div>
            </div>
          </fieldset>
        </div>

        <div className="sec">
          <div className="sec-head">
            <h3>Pricing in Omni</h3>
            <span className="note">Omni only. Autotask never sees these</span>
          </div>
          <fieldset className="form2 plain" disabled={!manages}>
            <label className="field">
              <span>Floor</span>
              <PriceBox id="f-floor" label="Floor" value={floor} onChange={setFloor} />
              <span className="hint">A dealer can quote down to the floor. Lower than that, Phoneware approves the quote first.</span>
            </label>
            <label className="field">
              <span>Reseller buy price</span>
              <PriceBox id="f-buy" label="Reseller buy price" value={buy} placeholder="Not set" onChange={setBuy} />
              <span className="hint">What a Reseller pays Phoneware. Resellers can{"\u2019"}t quote it until it has one.</span>
            </label>
          </fieldset>
          {formError ? (
            <p className="err" role="alert" data-testid="form-error">
              {formError}
            </p>
          ) : null}
        </div>

        <div className="sec" data-testid="sheet-onebill" data-state={p.billingLink.state}>
          <div className="sec-head">
            <h3>OneBill product</h3>
            <LinkChip p={p} />
            <span className="note">Saves as soon as you choose</span>
          </div>
          <OneBillBlock p={p} />
        </div>

        <div className="sec" data-testid="sheet-enable">
          <div className="sec-head">
            <h3>Dealers see it</h3>
            <span className="note">Saves as soon as you switch it</span>
          </div>
          <div className="switchrow">
            <label className="switch">
              <input type="checkbox" aria-label="Dealers see it" data-testid="sheet-enable-switch" checked={p.enabled} disabled={busy || !manages} onChange={(e) => void enable(p, e.target.checked, "sheet")} />
              <i />
            </label>
            <b>{p.enabled ? (reasons.length ? "Switched on, but dealers can\u2019t quote it" : "Dealers see it and can quote it") : "Switched off"}</b>
          </div>
          {refused === p.id ? (
            <div className="callout stop" role="alert" data-testid="enable-refused">
              <Icon name="triangle-alert" size={15} />
              <span>
                <b>Omni didn{"\u2019"}t switch it on.</b> Dealers can quote a product only when all of these hold.
              </span>
            </div>
          ) : null}
          {reasons.length ? (
            <ul className="reasons" data-testid="enable-reasons">
              {reasons.map((r) => (
                <li key={r}>
                  <Icon name="triangle-alert" size={14} />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="reasons" data-testid="enable-ok">
              <li className="ok">
                <Icon name="circle-check" size={14} />
                <span>
                  Billing set, active, in Autotask and linked to OneBill
                  {p.autotaskSync.state === "changed_in_autotask" ? ". The Autotask change leaves Active alone, so dealers keep quoting Omni\u2019s values" : ""}
                </span>
              </li>
            </ul>
          )}
        </div>
      </div>
    </Sheet>
  );
}
